import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import {
	DownloadGroupName,
	DownloadGroupStatus,
	DownloadMode,
	DownloadStatus,
	DownloadManagerState,
	DownloadManagerItemState,
} from '../reducer';
import NativeApi, { DownloadPriority, NativeDownloadError, isNativeUnzipFailure } from '../../../utils/nativeApi';
import UserAgentUtils from '../../../utils/userAgent';
import DriveItemUtils from '../../../utils/driveItem';
import UrlUtils from '../../../utils/url';
import Utils, { downloadsFolder, tempFolder } from '../../../utils';
import { Api } from '../../../typings/api';
import { createNotification } from '../../NotificationBar/actions';
import ApiUtils from '../../../utils/api';
import DownloadManagerUtils from '../../../utils/downloadManager';
import DownloadDiskVerification from '../../../utils/downloadDiskVerification';
import { getRequiredItemIds } from '../../../utils/requiredItems';
import {
	logOfflineDownload,
	isOfflineDownloadDebugEnabled,
	OfflineDownloadDebugAction,
	OfflineDownloadPhase,
	OfflineDownloadReason,
	OfflineDownloadDecision,
	OfflineDownloadBranch,
	getBackfillReason,
	getDependencyProgressSnapshot,
	getUpdatableDependencyDetails,
} from '../../../utils/offlineDownloadDebug';
import { isCancelledOperation, shouldIgnoreGallerySessionWork, getGallerySessionId } from '../../../utils/sessionCancellation';
import { checkStaleOfflineDownloadsAfterSync } from '../../StaleOfflineCleanup/actions';
import { getOrFetchDriveItemMeta, updateDriveItem, receiveDriveItems } from '../../DriveItemList/actions';
import IndexedDBManager from '../../../utils/indexedDB/manager';
import {
	transformTemporaryDownload,
	updateDependents,
	startDownloadGroup,
	finishDownloadGroup,
	pauseDownloadGroup,
	addItem,
	updateDownloadStatus,
	updateDownload,
	setRequestId,
	recordDownloadProgress,
	removePendingSelectedDownloadItem,
	resetTotalSelectedDownloadsInManager,
	addDownloadInProgressItem,
	removeDownloadInProgressItem,
	addDownloadInProgressDependency,
	removeDownloadInProgressDependency,
	invalidateDownloadConfirmation,
	setInitReconcileInProgress,
} from './store';
import StatsUtils from '../../../utils/statistics';
import allSettled from 'promise.allsettled';

import { nativeDelete } from './delete';
import { DriveItemInfo } from '../../DriveItemList/reducer';
const Q = require('q');

const requestWebDownloadItem = (driveItem: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { galleryInfos } = getState();
	const { config } = galleryInfos;

	if (Utils.isMaster(config)) {
		window.open(UrlUtils.getItemOriginalFileUrl(config, driveItem.originalFile), '_blank');
	} else {
		window.location.href = UrlUtils.getAttachmentUrl(config, driveItem.id);
	}
};

const initialProcessedIds = () => new Set<number>();

// The native layer rejects with undefined or {}, both of which log as nothing.
enum ErrorDetail {
	Undefined = 'undefined',
	EmptyObject = 'empty_object',
}

const describeError = (error: {} | undefined): string => {
	if (error === undefined) {
		return ErrorDetail.Undefined;
	}

	if (typeof error === 'object' && error !== null && Object.keys(error).length === 0) {
		return ErrorDetail.EmptyObject;
	}

	return String(error);
};

const shouldUseImportantOfflineShortcut = (
	downloadManager: DownloadManagerState,
	driveItem: Api.DriveItem,
	isImportant: boolean,
): boolean => {
	if (!isImportant) {
		return false;
	}

	const managerItem = downloadManager.items[driveItem.id];

	if (!managerItem || managerItem.status !== DownloadStatus.NATIVE_DOWNLOADED) {
		return false;
	}

	if (managerItem.dependencies.length === 0 && !managerItem.dependenciesResolved) {
		return false;
	}

	if (!DownloadManagerUtils.isItemDownloaded(downloadManager, driveItem.id)) {
		return false;
	}

	return DriveItemUtils.isUpToDateWithDependencies(driveItem, downloadManager, managerItem.version);
};

const isImportantAssetComplete = (
	downloadManager: DownloadManagerState,
	driveItem: Api.DriveItem | undefined,
	managerItem: DownloadManagerItemState | undefined,
): boolean => {
	if (!driveItem || !managerItem) {
		return false;
	}

	if (shouldUseImportantOfflineShortcut(downloadManager, driveItem, true)) {
		return true;
	}

	return DownloadManagerUtils.isItemLogicallyDownloaded(downloadManager, driveItem.id)
		&& DriveItemUtils.isUpToDateWithDependencies(driveItem, downloadManager, managerItem.version);
};

const finalizeUpdatableDownload = (
	driveItemId: number,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	await dispatch(repairUnconfirmedDownloads());
	await dispatch(removePendingSelectedDownload(driveItemId));
	await dispatch(removeDownloadInProgressItem(driveItemId));
};

const reconcileSelectedDownloadGroupAfterFailure = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
		const { downloadManager } = getState();
		const { selected } = downloadManager.downloadGroups;

		if (selected.status !== DownloadGroupStatus.Started) {
			return;
		}

		const pendingCount = Object.keys(downloadManager.pendingSelectedDownloads).length;
		const inProgressCount = downloadManager.downloadInProgressItems.length;

		if (pendingCount === 0 && inProgressCount === 0) {
			dispatch(finishDownloadGroup(DownloadGroupName.SELECTED));
			return;
		}

		if (pendingCount > 0 && inProgressCount === 0) {
			dispatch(pauseDownloadGroup(DownloadGroupName.SELECTED));
		}
	};

const releaseFailedDownloadRequest = (
	driveItem: Api.DriveItem,
	requestedMode: DownloadMode,
	isDependency: boolean,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	if (requestedMode === DownloadMode.Requested && !isDependency) {
		await dispatch(removeDownloadInProgressItem(driveItem.id));
	}

	if (requestedMode === DownloadMode.Dependency || isDependency) {
		await dispatch(removeDownloadInProgressDependency(driveItem.id));
	}

	const { downloadManager } = getState();
	const pendingSelectedIds = Object.keys(downloadManager.pendingSelectedDownloads);

	if (pendingSelectedIds.includes(String(driveItem.id)) && pendingSelectedIds.length === 1) {
		await dispatch(removePendingSelectedDownload(driveItem.id));
		return;
	}

	dispatch(reconcileSelectedDownloadGroupAfterFailure());
};

const requestMobileAppDownload = (
	driveItem: Api.DriveItem,
	requestedMode: DownloadMode,
	dependentId?: number,
	// Not "server version is newer". Means: reconcile an item that may already exist in the
	// download manager (backfill, important batch, user Update). false = treat as first download.
	isUpdatable = false,
	isDependency = false,
	processedIds: Set<number> = initialProcessedIds(),
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const gallerySessionId = getGallerySessionId();

	return Q.Promise(async (resolve, reject) => {
		try {
			if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
				resolve();
				return;
			}

			if (!driveItem || !driveItem.id) {
				console.error('requestMobileAppDownload: invalid driveItem', driveItem);
				resolve();
				return;
			}

			const { downloadManager } = getState();
			const itemFromDownloadManager = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id] : undefined;
			const wasDownloadInProgressItem = downloadManager.downloadInProgressItems.includes(driveItem.id);
			const wasDownloadInProgressDependency = downloadManager.downloadInProgressDependencies.includes(driveItem.id);
			const resumeStaleNativeZip = !!itemFromDownloadManager
				&& !wasDownloadInProgressItem
				&& !wasDownloadInProgressDependency
				&& DownloadManagerUtils.hasStaleNativeZipStatus(downloadManager, driveItem.id);

			if (processedIds.has(driveItem.id)) {
				logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.RequestMobileAppDownload, driveItem.id, {
					reason: OfflineDownloadReason.AlreadyProcessedInBatch,
					isDependency,
				});
				resolve();
				return;
			}

			processedIds.add(driveItem.id);

			if (requestedMode === DownloadMode.Requested && !isDependency) {
				await dispatch(addDownloadInProgressItem(driveItem.id));
			}

			if (requestedMode === DownloadMode.Dependency || isDependency) {
				await dispatch(addDownloadInProgressDependency(driveItem.id));
			}

			if (itemFromDownloadManager) {
				const { mode, version, status } = itemFromDownloadManager;

				if (status === DownloadStatus.STARTING_DELETION) {
					logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.RequestMobileAppDownload, driveItem.id, {
						reason: OfflineDownloadReason.ItemMarkedForDeletion,
					});

					// Item is being deleted, not downloaded — release the in-progress marker set above.
					if (requestedMode === DownloadMode.Requested && !isDependency) {
						await dispatch(removeDownloadInProgressItem(driveItem.id));
					}

					if (requestedMode === DownloadMode.Dependency || isDependency) {
						await dispatch(removeDownloadInProgressDependency(driveItem.id));
					}

					resolve();
					return;
				}

				if (status === DownloadStatus.ERROR) {
					try {
						await dispatch(downloadItemWithDependencies(
							driveItem,
							requestedMode,
							dependentId,
							processedIds,
							isDependency,
							resumeStaleNativeZip,
						));
					} catch (error) {
						console.error(`Download error for item id ${driveItem.id}:`, error);
						await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
					}
					resolve();
					return;
				}

				if (mode === DownloadMode.Temp) {
					await dispatch(transformTemporaryDownload(driveItem, itemFromDownloadManager, dependentId));
					await dispatch(removePendingSelectedDownload(driveItem.id));
					await dispatch(removeDownloadInProgressItem(driveItem.id));
					resolve();
					return;
				// Sometimes an item, which 'requestedMode' is 'required', can be downloaded/updated as a dependency of another item
				} else if (requestedMode === DownloadMode.Dependency || isDependency) {
					try {
						if (downloadManager.downloadInProgressDependencies && downloadManager.downloadInProgressDependencies.includes(driveItem.id)) {
							await dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
							resolve();
							return;
						}

						await dispatch(downloadItemWithDependencies(
							driveItem,
							requestedMode,
							dependentId,
							processedIds,
							isDependency,
							resumeStaleNativeZip,
						));
						await dispatch(removeDownloadInProgressDependency(driveItem.id));
					} catch (error) {
						console.error(`Download error for updatable dependency id ${driveItem.id}:`, error);
						await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
					}
					resolve();
					return;
				} else {
					if (DownloadManagerUtils.isLatestVersion(driveItem.version, version) && !isUpdatable) {
						if (!DownloadManagerUtils.isItemDownloaded(downloadManager, driveItem.id)) {
							try {
								await dispatch(downloadItemWithDependencies(
									driveItem,
									requestedMode,
									dependentId,
									processedIds,
									isDependency,
									resumeStaleNativeZip,
								));
								await dispatch(removePendingSelectedDownload(driveItem.id));
								await dispatch(removeDownloadInProgressItem(driveItem.id));
							} catch (error) {
								console.error(`Download error for item id ${driveItem.id}:`, error);
								await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
							}
							resolve();
							return;
						}

						try {
							await dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
							await dispatch(removePendingSelectedDownload(driveItem.id));
							await dispatch(removeDownloadInProgressItem(driveItem.id));
						} catch (error) {
							console.error(`Update error for item id ${driveItem.id}:`, error);
							await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
						}
						resolve();
						return;
					}

					// Reconcile path (isUpdatable): item may already be on disk. Skip re-download only when
					// the asset and its dependencies are logically complete, versions match the server,
					// and no dependency needs updating. Disk confirmation is handled separately by repair.
					if (
						isUpdatable
						&& !isDependency
						&& DownloadManagerUtils.isItemLogicallyDownloaded(downloadManager, driveItem.id)
						&& DriveItemUtils.isUpToDateWithDependencies(driveItem, downloadManager, version)
					) {
						logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.RequestMobileAppDownload, driveItem.id, {
							reason: OfflineDownloadReason.LogicallyCompleteNoZip,
							confirmed: DownloadManagerUtils.isDownloadConfirmed(downloadManager, driveItem.id),
						});
						try {
							await dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
							await dispatch(finalizeUpdatableDownload(driveItem.id));
						} catch (error) {
							console.error(`Update error for item id ${driveItem.id}:`, error);
							await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
						}
						resolve();
						return;
					}

					if (isUpdatable) {
						if (
							!isDependency
							&& wasDownloadInProgressItem
							&& DownloadManagerUtils.isNativeZipInProgress(getState().downloadManager, driveItem.id)
						) {
							logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.RequestMobileAppDownload, driveItem.id, {
								reason: OfflineDownloadReason.NativeZipInProgress,
							});
							resolve();
							return;
						}

						try {
							await dispatch(downloadItemWithDependencies(
								driveItem,
								requestedMode,
								dependentId,
								processedIds,
								isDependency,
								resumeStaleNativeZip,
							));
							await dispatch(finalizeUpdatableDownload(driveItem.id));
						} catch (error) {
							console.error(`Download error for updatable item id ${driveItem.id}:`, error);
							await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
						}
						resolve();
						return;
					}
				}

			} else {
				dispatch(addItem(driveItem, requestedMode, [], []));

				try {
					await dispatch(downloadItemWithDependencies(
						driveItem,
						requestedMode,
						dependentId,
						processedIds,
						isDependency,
						resumeStaleNativeZip,
					));
					await dispatch(removePendingSelectedDownload(driveItem.id));
					await dispatch(removeDownloadInProgressItem(driveItem.id));
					await dispatch(removeDownloadInProgressDependency(driveItem.id));
				} catch (error) {
					console.error(`Download error for new item id ${driveItem.id}:`, error);
					await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
				}
				resolve();
				return;
			}

			resolve();
		} catch (error) {
			console.error(`requestMobileAppDownload error for item id ${driveItem.id}:`, error);
			await dispatch(releaseFailedDownloadRequest(driveItem, requestedMode, isDependency));
			reject(error);
		}
	});
};

const downloadItemWithDependencies = (
	driveItem: Api.DriveItem,
	mode: DownloadMode,
	dependentId?: number,
	processedIds: Set<number> = initialProcessedIds(),
	isDependency = false,
	resumeStaleNativeZip = false,
	) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return Q.Promise.resolve();
		}

		const { downloadManager } = getState();
		const itemFromDownloadManager = downloadManager.items[driveItem.id];

		let allDependenciesPromise: Q.Promise<Api.Dependency[]> = Q.Promise.resolve([]);
		let dependenciesResolved = false;

		if (isDependency) {
			dependenciesResolved = true;
		} else {
			allDependenciesPromise = Q.Promise.resolve(dispatch(getDependenciesWithVersions(driveItem, Api.OutcomingsLevel.ALL)));
			dependenciesResolved = true;
		}

		return allDependenciesPromise
			.then((allDependencies) => {
				const currentDependentsIds = itemFromDownloadManager ? itemFromDownloadManager.dependents : [];

				const dependentsIds: number[] = dependentId !== undefined
				&& !currentDependentsIds.includes(dependentId)
					? [dependentId, ...currentDependentsIds]
					: currentDependentsIds;

				const allDependenciesIds = !isDependency ? allDependencies.map(dependency => dependency.id) : [];
				const isLatestVersion = itemFromDownloadManager ? DownloadManagerUtils.isLatestVersion(driveItem.version, itemFromDownloadManager.version) : true;

				let promise: Q.Promise<{} | void> = Q.Promise.resolve();

				if (itemFromDownloadManager && [DownloadStatus.INIT, DownloadStatus.ERROR].includes(itemFromDownloadManager.status)) {
					logOfflineDownload(OfflineDownloadDebugAction.NativeZip, OfflineDownloadPhase.DownloadItemWithDependencies, driveItem.id, {
						branch: itemFromDownloadManager.status === DownloadStatus.ERROR ? OfflineDownloadBranch.ErrorRetry : OfflineDownloadBranch.Init,
						isDependency,
						parentId: dependentId,
					});
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.STARTING_DOWNLOAD)))
						.then(() => dispatch(nativeDownload(driveItem, mode)))
						.then(() => dispatch(updateDownload(
							driveItem,
							mode,
							allDependenciesIds,
							dependentsIds,
							dependenciesResolved,
						)));
				} else if (
					itemFromDownloadManager
					&& (
						!isLatestVersion
						|| ![DownloadStatus.NATIVE_DOWNLOADED].includes(itemFromDownloadManager.status)
					)
					&& (
						resumeStaleNativeZip
						|| !DownloadManagerUtils.isNativeZipInProgress(getState().downloadManager, driveItem.id)
					)
				) {
					logOfflineDownload(OfflineDownloadDebugAction.NativeZip, OfflineDownloadPhase.DownloadItemWithDependencies, driveItem.id, {
						branch: isLatestVersion ? OfflineDownloadBranch.ResumeOrMissingStatus : OfflineDownloadBranch.VersionUpdate,
						isDependency,
						parentId: dependentId,
						storedVersion: itemFromDownloadManager.version,
						apiVersion: driveItem.version,
						resumeStaleNativeZip,
					});
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, isLatestVersion ? DownloadStatus.RESUMING_DOWNLOAD : DownloadStatus.STARTING_UPDATE)))
						.then(() => dispatch(updateDependents(driveItem, itemFromDownloadManager, mode, dependentId)))
						.then(() => dispatch(nativeDownload(driveItem, mode, !isLatestVersion)))
						.then(() => dispatch(updateDownload(
							driveItem,
							mode,
							allDependenciesIds,
							dependentsIds,
							dependenciesResolved,
						)));
				} else if (itemFromDownloadManager
					&& ![
						DownloadStatus.STARTING_DOWNLOAD,
						DownloadStatus.NATIVE_DOWNLOADING,
						DownloadStatus.STARTING_UPDATE,
						DownloadStatus.UPDATING,
						DownloadStatus.RESUMING_DOWNLOAD
					].includes(itemFromDownloadManager.status)) {
					logOfflineDownload(OfflineDownloadDebugAction.AlreadyComplete, OfflineDownloadPhase.DownloadItemWithDependencies, driveItem.id, {
						isDependency,
						parentId: dependentId,
						depCount: allDependenciesIds.length,
					});
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.NATIVE_DOWNLOADED)))
						.then(() => dispatch(updateDependents(driveItem, itemFromDownloadManager, mode, dependentId)))
						.then(() => dispatch(updateDownload(
							driveItem,
							mode,
							allDependenciesIds,
							dependentsIds,
							dependenciesResolved,
						)));
				}

				if (!isDependency && itemFromDownloadManager
					&& !resumeStaleNativeZip
					&& DownloadManagerUtils.isNativeZipInProgress(getState().downloadManager, driveItem.id)) {
					logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.DownloadItemWithDependencies, driveItem.id, {
						reason: OfflineDownloadReason.NativeZipInProgress,
					});
					return Q.Promise.resolve();
				}

				if (!isDependency) {
					logOfflineDownload(OfflineDownloadDebugAction.DepWalk, OfflineDownloadPhase.DownloadItemWithDependencies, driveItem.id, {
						depIds: allDependencies.map(dependency => dependency.id).join(','),
						depCount: allDependencies.length,
					});
					// Ensure that each dependency is processed sequentially
					promise = allDependencies.reduce((acc, updatedDependency) => {

						return acc
							.then(() => dispatch(getOrFetchDriveItemMeta(updatedDependency.id, false)))
							.then((dependency: Api.DriveItem) => {
								if (!dependency || !dependency.id) {
									console.warn(
										`Skipping dependency download: meta not available for dependency ${updatedDependency.id}`
									);
									return Q.Promise.resolve();
								}

								dependency = {
									...dependency,
									version: updatedDependency.version
								};
								const dependencyFromDownloadManager = downloadManager.items[dependency.id];
								const downloadMode = DownloadManagerUtils.isPersistentItem(dependencyFromDownloadManager)
									? dependencyFromDownloadManager.mode
									: DownloadMode.Dependency;

								if (dependencyFromDownloadManager) {
									return dispatch(requestMobileAppDownload(dependency, downloadMode, driveItem.id, true, true, processedIds));
								} else {
									return dispatch(requestMobileAppDownload(dependency, DownloadMode.Dependency, driveItem.id, false, true, processedIds));
								}
							});
					}, promise);
				}

				return promise;
			})
			.catch((e) => {
				if (shouldIgnoreGallerySessionWork(gallerySessionId) || isCancelledOperation(e)) {
					return Q.Promise.reject({});
				}

				if (isNativeUnzipFailure(e)) {
					dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.ERROR));
					dispatch(createNotification('error.unzip_fail', { id: driveItem.id }));
					return Q.Promise.reject({});
				}

				console.error(
					`Error in downloadItemWithDependencies for item ${driveItem.id}: ${describeError(e)}`,
					e,
				);
				dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.ERROR));
				dispatch(createNotification('error.download_fail', { id: driveItem.id }));
				dispatch(nativeDelete(driveItem));
				return Q.Promise.reject({});
			});
	};

const nativeDownload = (driveItem: Api.DriveItem, mode: DownloadMode, isUpdating = false) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {

	logOfflineDownload(OfflineDownloadDebugAction.NativeZip, OfflineDownloadPhase.NativeDownload, driveItem.id, {
		mode,
		isUpdating,
		zip: `${driveItem.id}.zip`,
	});

	const { galleryInfos } = getState();
	const { config } = galleryInfos;
	const fileName = `${driveItem.id}.zip`;
	const url = UrlUtils.getResourceZipUrl(config, driveItem.id);
	const destFolder = mode === DownloadMode.Temp ? tempFolder : downloadsFolder;
	const downloadRequest = NativeApi.downloadFile(galleryInfos.config, url, destFolder, fileName, DownloadPriority.High);

	dispatch(setRequestId(driveItem.id, downloadRequest.requestId, isUpdating));

	const minimumTimeBetweenProgress = 500;
	let lastProgressTime = 0;
	return downloadRequest.promise
		.progress(progress => {
			const isVisibleDownload = [DownloadMode.Requested, DownloadMode.Temp].includes(mode);
			const currentTime = Date.now();
			const timeSinceLastProgress = currentTime - lastProgressTime;
			const isEnoughTimeSinceLastProgress = timeSinceLastProgress > minimumTimeBetweenProgress;
			if (isEnoughTimeSinceLastProgress && isVisibleDownload) {
				lastProgressTime = currentTime;
				dispatch(recordDownloadProgress(progress, driveItem.id));
			}
		})
		.then(() => NativeApi.unzipFile(`${destFolder}/${fileName}`))
		.then(() => NativeApi.removeFile(`${destFolder}/${fileName}`))
		.then(() => DownloadDiskVerification.isDriveItemOnDisk(driveItem))
		.then((onDisk) => {
			logOfflineDownload(
				onDisk ? OfflineDownloadDebugAction.DiskConfirm : OfflineDownloadDebugAction.DiskRedownload,
				OfflineDownloadPhase.NativeDownloadPostUnzip,
				driveItem.id,
				{ onDisk },
			);

			if (!onDisk) {
				return Q.Promise.reject(NativeDownloadError.UnzipFailed);
			}

			return dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.NATIVE_DOWNLOADED))
				.then(() => {
					const { downloadManager } = getState();
					const managerItem = downloadManager.items[driveItem.id];

					if (managerItem) {
						return dispatch(updateDownload(
							driveItem,
							mode,
							managerItem.dependencies,
							managerItem.dependents,
							undefined,
							true,
						));
					}
				});
		})
		.catch((error) => {
			if (isNativeUnzipFailure(error)) {
				return Q.Promise.reject(NativeDownloadError.UnzipFailed);
			}

			return Q.Promise.reject(error);
		});
};

const getDependenciesExclusions = (state: StoreState): { ids: number[], types: Api.OutcomingType[] } => {
	const {
		galleryInfos : {
			config : {
				dependencies: { exclude } = { exclude: { ids: [], types: [] } },
			},
		},
	} = state;

	return exclude;
};

const isExcludedOutcoming = (
	outcoming: Api.Outcoming,
	dependenciesExclusionIds: number[],
	dependenciesExclusionTypes: Api.OutcomingType[],
): boolean =>
	dependenciesExclusionIds.includes(outcoming.id) || dependenciesExclusionTypes.includes(outcoming.type);

const toDependency = (outcoming: Api.Outcoming): Api.Dependency => ({
	id: outcoming.id,
	version: Number(outcoming.version),
	type: outcoming.type,
});

const getDependencyIds = (driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	const { ids: dependenciesExclusionIds, types: dependenciesExclusionTypes } = getDependenciesExclusions(getState());

	switch (driveItem.type) {
		case Api.DriveItemType.Publication:
		case Api.DriveItemType.Component:
		case Api.DriveItemType.Template:
		case Api.DriveItemType.Gallery:
			const response = await ApiUtils.getOutcomings(driveItem.id);
			if (response.outcomings) {
				const dependencyIds =  response.outcomings
					.filter(outcoming => !isExcludedOutcoming(outcoming, dependenciesExclusionIds, dependenciesExclusionTypes))
					.map(outcoming => outcoming.id);
				return dependencyIds;
			}
			return [];
		default:
			return Q.Promise.resolve([]);
	}
};

const flattenOutcomingsToDependencies = (
	outcomings: Api.Outcoming[],
	dependenciesExclusionIds: number[],
	dependenciesExclusionTypes: Api.OutcomingType[],
): Api.Dependency[] => {
	const dependenciesById: { [id: number]: Api.Dependency } = {};

	const visit = (nodes: Api.Outcoming[]) => {
		nodes.forEach((outcoming) => {
			if (isExcludedOutcoming(outcoming, dependenciesExclusionIds, dependenciesExclusionTypes)) {
				return;
			}

			const dependency = toDependency(outcoming);
			const existing = dependenciesById[outcoming.id];

			if (!existing || dependency.version > existing.version) {
				dependenciesById[outcoming.id] = dependency;
			}

			if (outcoming.outcomings && outcoming.outcomings.length > 0) {
				visit(outcoming.outcomings);
			}
		});
	};

	visit(outcomings);

	return Object.keys(dependenciesById).map(id => dependenciesById[Number(id)]);
};

const getDependenciesWithVersions = (
	driveItem: Api.DriveItem,
	level?: string,
	includeNested = false,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<Api.Dependency[]> => {
	if (!driveItem || driveItem.type === undefined) {
		return [];
	}

	const { ids: dependenciesExclusionIds, types: dependenciesExclusionTypes } = getDependenciesExclusions(getState());

	switch (driveItem.type) {
		case Api.DriveItemType.Publication:
		case Api.DriveItemType.Component:
		case Api.DriveItemType.Template:
		case Api.DriveItemType.Gallery:
			const updatedDependenciesExclusionTypes = [...dependenciesExclusionTypes];

			if (!updatedDependenciesExclusionTypes.includes(Api.OutcomingType.Directory)) {
				updatedDependenciesExclusionTypes.push(Api.OutcomingType.Directory);
			}

			const response = await ApiUtils.getOutcomings(driveItem.id, level, updatedDependenciesExclusionTypes, dependenciesExclusionIds);

			if (response.outcomings) {
				if (includeNested) {
					return flattenOutcomingsToDependencies(
						response.outcomings,
						dependenciesExclusionIds,
						dependenciesExclusionTypes,
					);
				}

				return response.outcomings
					.filter(outcoming => !isExcludedOutcoming(outcoming, dependenciesExclusionIds, dependenciesExclusionTypes))
					.map(toDependency);
			}

			return [];
		default:
			return Q.Promise.resolve([]);
	}
};

const updateDriveItemWithDependencies = (driveItem: DriveItemInfo, isManualUpdate: boolean = false) =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		if (!driveItem) {
			return;
		}

		return dispatch(getDependenciesWithVersions(driveItem, Api.OutcomingsLevel.ALL, true))
			.then(dependenciesVersion => {
				if (!isManualUpdate) {
					driveItem = {
						...driveItem,
						dependencies: dependenciesVersion
					};
					void dispatch(updateDriveItem(driveItem));
					return Promise.resolve();
				} else {
					const { isInGallery } = driveItem;
					return dispatch(getOrFetchDriveItemMeta(driveItem.id, isInGallery))
						.then((updatedDriveItem: Api.DriveItem) => {
							updatedDriveItem = {
								...updatedDriveItem,
								dependencies: dependenciesVersion
							};
							void dispatch(updateDriveItem(updatedDriveItem));
						});
				}
			})
			.catch(error => {
				console.error('Failed to fetch dependencies version:', error);
			});
	};

const refreshDownloadedDependencyVersions = () =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return;
		}

		const { downloadManager, driveList: { driveItems } } = getState();
		const downloadedIds = DownloadManagerUtils.getRequestedItemIds(downloadManager);

		if (downloadedIds.length === 0) {
			return;
		}

		logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.UpdateDriveItemWithDependencies, undefined, {
			requestedIds: downloadedIds.join(','),
		});

		const updatePromises = downloadedIds
			.map(id => driveItems[id] ? dispatch(updateDriveItemWithDependencies(driveItems[id])) : Promise.resolve());

		await Promise.all(updatePromises);

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return;
		}

		if (isOfflineDownloadDebugEnabled()) {
			const refreshedManager = getState().downloadManager;
			downloadedIds.forEach((id) => {
				const driveItem = getState().driveList.driveItems[id] || driveItems[id];
				if (driveItem) {
					logOfflineDownload(OfflineDownloadDebugAction.DepProgress, OfflineDownloadPhase.AfterOutcomingsRefresh, id, {
						updatableDeps: getUpdatableDependencyDetails(driveItem, refreshedManager),
						confirmed: DownloadManagerUtils.isDownloadConfirmed(refreshedManager, id),
					});
				}
			});
		}
	};

const getInitialDependenciesUpdates = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const gallerySessionId = getGallerySessionId();

	if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
		return;
	}

	dispatch(setInitReconcileInProgress(true));

	try {
		logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.GetInitialDependenciesUpdates);
		await dispatch(syncOfflineDependenciesAfterContentRefresh({
			resumeInterrupted: true,
			autoUpdateImportant: true,
		}));
		logOfflineDownload(OfflineDownloadDebugAction.PhaseEnd, OfflineDownloadPhase.GetInitialDependenciesUpdates);
	} catch (error) {
		console.error('Error in getInitialDependenciesUpdates:', error);
	} finally {
		dispatch(setInitReconcileInProgress(false));
	}
};

interface BackfillIncompleteDependenciesOptions {
	includeUpdatableDependencies?: boolean;
	importantOnly?: boolean;
}

const backfillIncompleteDependencies = (
	options: BackfillIncompleteDependenciesOptions = {},
) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const includeUpdatableDependencies = options.includeUpdatableDependencies !== false;
		const importantOnly = options.importantOnly === true;
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return Q.Promise.resolve();
		}

		logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.BackfillIncompleteDependencies);
		const { downloadManager, driveList: { driveItems }, galleryInfos: { config: { importantAssets } } } = getState();
		const { items } = downloadManager;
		const enabledImportantTags = DownloadManagerUtils.getEnabledImportantTags(importantAssets);

		const isInImportantSubtree = (driveItemId: number) =>
			DownloadManagerUtils.isInImportantSubtree(driveItems, enabledImportantTags, String(driveItemId));

		const itemIds = Object.keys(items)
			.map(id => parseInt(id, 10))
			.filter(id => {
				const item = items[id];
				const driveItem = driveItems[id];

				if (!item || !driveItem || !DriveItemUtils.isDownloadableItemType(driveItem)) {
					return false;
				}

				if (!DownloadManagerUtils.isPersistentItem(item)) {
					return false;
				}

				if (item.status === DownloadStatus.STARTING_DELETION) {
					return false;
				}

				if (DownloadManagerUtils.isNativeZipInProgress(downloadManager, id)
					&& !DownloadManagerUtils.hasStaleNativeZipStatus(downloadManager, id)) {
					return false;
				}

				if (downloadManager.downloadInProgressItems.includes(id) && item.status !== DownloadStatus.ERROR) {
					return false;
				}

				if (importantOnly && !isInImportantSubtree(id)) {
					return false;
				}

				if (DownloadManagerUtils.hasIncompleteDependencies(downloadManager, id)) {
					return true;
				}

				if (DownloadManagerUtils.hasStaleNativeZipStatus(downloadManager, id)) {
					return true;
				}

				if (!includeUpdatableDependencies) {
					return false;
				}

				if (DriveItemUtils.hasUpdatableDependencies(driveItem, downloadManager)) {
					return true;
				}

				return importantOnly
					&& !DownloadManagerUtils.isLatestVersion(driveItem.version, item.version);
			});

		if (itemIds.length === 0) {
			logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.BackfillIncompleteDependencies, undefined, {
				reason: OfflineDownloadReason.NothingToBackfill,
			});
			return Q.Promise.resolve();
		}

		if (isOfflineDownloadDebugEnabled()) {
			itemIds.forEach((id) => {
				const driveItem = driveItems[id];
				const progress = getDependencyProgressSnapshot(downloadManager, id);
				logOfflineDownload(OfflineDownloadDebugAction.BackfillQueue, OfflineDownloadPhase.BackfillIncompleteDependencies, id, {
					reason: getBackfillReason(downloadManager, driveItem),
					depProgress: progress ? `${progress.downloaded}/${progress.total}` : 'n/a',
				});
			});
		}

		const downloadPromises = itemIds.map(id =>
			dispatch(requestMobileAppDownload(
				driveItems[id],
				items[id].mode,
				undefined,
				true,
				false,
				undefined,
			))
		);

		return allSettled(downloadPromises)
			.then(() => {
				logOfflineDownload(OfflineDownloadDebugAction.PhaseEnd, OfflineDownloadPhase.BackfillIncompleteDependencies, undefined, {
					queuedCount: itemIds.length,
				});
				return ({} as {});
			});
	};

const repairUnconfirmedDownloads = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return Q.Promise.resolve();
		}

		logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.RepairUnconfirmedDownloads);
		const { downloadManager, driveList: { driveItems } } = getState();
		const { items } = downloadManager;
		let verifyCount = 0;

		const repairPromises = Object.keys(items).map((key) => {
			const driveItemId = parseInt(key, 10);
			const managerItem = items[driveItemId];
			const driveItem = driveItems[driveItemId];

			if (!DownloadManagerUtils.hasUnconfirmedDownload(downloadManager, driveItemId)) {
				return Q.Promise.resolve();
			}

			if (!driveItem || !DriveItemUtils.isDownloadableItemType(driveItem)) {
				return Q.Promise.resolve();
			}

			if (!DownloadManagerUtils.isItemLogicallyDownloaded(downloadManager, driveItemId)) {
				logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.RepairUnconfirmedDownloads, driveItemId, {
					reason: OfflineDownloadReason.NotLogicallyDownloaded,
				});
				return Q.Promise.resolve();
			}

			verifyCount += 1;
			logOfflineDownload(OfflineDownloadDebugAction.DiskCheck, OfflineDownloadPhase.RepairUnconfirmedDownloads, driveItemId, {
				mode: managerItem.mode,
			});

			return DownloadDiskVerification.isDriveItemOnDisk(driveItem)
				.then((onDisk) => {
					if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
						return Q.Promise.resolve();
					}

					if (onDisk) {
						logOfflineDownload(OfflineDownloadDebugAction.DiskConfirm, OfflineDownloadPhase.RepairUnconfirmedDownloads, driveItemId);
						dispatch(updateDownload(
							driveItem,
							managerItem.mode,
							managerItem.dependencies,
							managerItem.dependents,
							undefined,
							true,
						));
						return Q.Promise.resolve();
					}

					logOfflineDownload(OfflineDownloadDebugAction.DiskRedownload, OfflineDownloadPhase.RepairUnconfirmedDownloads, driveItemId, {
						reason: OfflineDownloadReason.MissingOnDisk,
					});
					dispatch(invalidateDownloadConfirmation(driveItem));
					return dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.ERROR))
						.then(() => dispatch(downloadItemWithDependencies(
							driveItem,
							managerItem.mode,
							undefined,
							initialProcessedIds(),
							false,
						)));
				});
		});

		return allSettled(repairPromises)
			.then(() => {
				logOfflineDownload(OfflineDownloadDebugAction.PhaseEnd, OfflineDownloadPhase.RepairUnconfirmedDownloads, undefined, {
					verifyCount,
				});
				return ({} as {});
			});
	};

interface ReconcileOfflineDownloadsOptions {
	resumeInterrupted?: boolean;
	autoUpdateImportant?: boolean;
}

const reconcileOfflineDownloadsAfterDependencyRefresh = (
	options: ReconcileOfflineDownloadsOptions = {},
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const gallerySessionId = getGallerySessionId();

	if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
		return;
	}

	const backfillThenRepair = async (backfillOptions: BackfillIncompleteDependenciesOptions) => {
		await dispatch(backfillIncompleteDependencies(backfillOptions));
		await dispatch(repairUnconfirmedDownloads());
	};

	// Init only: resume interrupted downloads for all items before queuing updates.
	if (options.resumeInterrupted) {
		await backfillThenRepair({ includeUpdatableDependencies: false });
	}

	// Auto-update version/deps for important items; regular items stay manual Update.
	if (options.autoUpdateImportant) {
		await backfillThenRepair({ includeUpdatableDependencies: true, importantOnly: true });
	}
};

const syncOfflineDependenciesAfterContentRefresh = (
	options: ReconcileOfflineDownloadsOptions = {},
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	await dispatch(refreshDownloadedDependencyVersions());

	if (shouldIgnoreGallerySessionWork(getGallerySessionId())) {
		return;
	}

	await dispatch(reconcileOfflineDownloadsAfterDependencyRefresh(options));

	if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
		dispatch(checkStaleOfflineDownloadsAfterSync());
	}
};

// Confirm downloads that are present on disk, without re-downloading. Runs on the offline init path;
// items not found stay unconfirmed for the next online repair.
const confirmDownloadsOnDisk = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return Q.Promise.resolve();
		}

		logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.ConfirmDownloadsOnDisk);
		const { downloadManager, driveList: { driveItems } } = getState();
		const { items } = downloadManager;

		const confirmPromises = Object.keys(items).map((key) => {
			const driveItemId = parseInt(key, 10);
			const managerItem = items[driveItemId];
			const driveItem = driveItems[driveItemId];

			if (!DownloadManagerUtils.hasUnconfirmedDownload(downloadManager, driveItemId)) {
				return Q.Promise.resolve();
			}

			if (!driveItem || !DriveItemUtils.isDownloadableItemType(driveItem)) {
				return Q.Promise.resolve();
			}

			if (!DownloadManagerUtils.isItemLogicallyDownloaded(downloadManager, driveItemId)) {
				return Q.Promise.resolve();
			}

			// Confirm only when the item's file and every dependency file are on disk. Offline a
			// missing dependency can't be re-downloaded, so confirming the parent would open it broken.
			const dependencyDriveItems = managerItem.dependencies.map(dependencyId => driveItems[dependencyId]);

			if (dependencyDriveItems.some(dependencyDriveItem => !dependencyDriveItem)) {
				return Q.Promise.resolve();
			}

			const filesToVerify = [driveItem, ...dependencyDriveItems];

			return Q.all(filesToVerify.map(item => DownloadDiskVerification.isDriveItemOnDisk(item, false)))
				.then((onDiskResults: boolean[]) => {
					if (shouldIgnoreGallerySessionWork(gallerySessionId) || !onDiskResults.every(Boolean)) {
						return Q.Promise.resolve();
					}

					logOfflineDownload(OfflineDownloadDebugAction.DiskConfirm, OfflineDownloadPhase.ConfirmDownloadsOnDisk, driveItemId);
					dispatch(updateDownload(
						driveItem,
						managerItem.mode,
						managerItem.dependencies,
						managerItem.dependents,
						undefined,
						true,
					));
					return Q.Promise.resolve();
				});
		});

		return allSettled(confirmPromises)
			.then(() => {
				logOfflineDownload(OfflineDownloadDebugAction.PhaseEnd, OfflineDownloadPhase.ConfirmDownloadsOnDisk);
				return ({} as {});
			});
	};

const reconcileIncompleteDownloads = () =>
	(dispatch: Dispatch<StoreState>): Q.Promise<{} | void> =>
		dispatch(backfillIncompleteDependencies())
			.then(() => dispatch(repairUnconfirmedDownloads()));

const downloadAll = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const {
		driveList,
		galleryInfos,
		galleryInfos: {
			config: {
				applicationGallery
			}
		}
	} = getState();
	StatsUtils.trackDownloadAll();
	dispatch(startDownloadGroup(DownloadGroupName.ALL));

	const sharedDirId = Utils.getSharedDirId(driveList, galleryInfos.config);

	// TODO: Armand asked that the download all should not download the shared dir content
	return dispatch(downloadRecursively(applicationGallery, DownloadMode.Requested, true))
		.finally(() => sharedDirId && dispatch(downloadRecursively(sharedDirId, DownloadMode.Requested, true)))
		.then(() => dispatch(reconcileIncompleteDownloads()))
		.finally(() => dispatch(finishDownloadGroup(DownloadGroupName.ALL)));
};

const downloadImportantAssets = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const gallerySessionId = getGallerySessionId();

	if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
		return Q.resolve({});
	}

	logOfflineDownload(OfflineDownloadDebugAction.PhaseStart, OfflineDownloadPhase.DownloadImportantAssets);
	const {
		galleryInfos: {
			config: {
				importantAssets,
			},
			template,
		},
		driveList: {
			driveItems
		},
		downloadManager,
	} = getState();

	const enabledImportantTags = DownloadManagerUtils.getEnabledImportantTags(importantAssets);
	const requiredItemIds = getRequiredItemIds(template);

	// Guard: Ensure driveItems is populated before attempting to find important items
	// This prevents issues when called before getAllDriveItems() completes
	if (!driveItems || Object.keys(driveItems).length === 0) {
		console.warn('downloadImportantAssets: driveItems is empty, skipping important downloads');
		return Q.resolve({});
	}

	let importantAssetIds: string[] = [];
	if (enabledImportantTags.length > 0) {
		const allDriveItemIds = Object.keys(driveItems);
		importantAssetIds = allDriveItemIds
			.filter(DownloadManagerUtils.filterImportantItems(driveItems, enabledImportantTags))
			.filter(importantAssetId => !requiredItemIds.has(parseInt(importantAssetId, 10)));
	}

	const needsDownload = importantAssetIds.some((importantAssetId) => {
		const id = parseInt(importantAssetId, 10);
		return !isImportantAssetComplete(downloadManager, driveItems[id], downloadManager.items[id]);
	});

	if (isOfflineDownloadDebugEnabled()) {
		importantAssetIds.forEach((importantAssetId) => {
			const id = parseInt(importantAssetId, 10);
			const driveItem = driveItems[id];
			const managerItem = downloadManager.items[id];
			let decision = OfflineDownloadDecision.Download;

			if (!driveItem || !managerItem) {
				decision = OfflineDownloadDecision.DownloadMissingMetaOrManager;
			} else if (shouldUseImportantOfflineShortcut(downloadManager, driveItem, true)) {
				decision = OfflineDownloadDecision.SkipShortcut;
			} else if (DownloadManagerUtils.isItemLogicallyDownloaded(downloadManager, id)
				&& DriveItemUtils.isUpToDateWithDependencies(driveItem, downloadManager, managerItem.version)) {
				decision = OfflineDownloadDecision.SkipLogicallyComplete;
			} else if (managerItem && !DownloadManagerUtils.isLatestVersion(driveItem.version, managerItem.version)) {
				decision = OfflineDownloadDecision.DownloadVersionUpdate;
			}

			logOfflineDownload(OfflineDownloadDebugAction.DepProgress, OfflineDownloadPhase.DownloadImportantAssetsDecision, id, {
				decision,
				confirmed: managerItem ? DownloadManagerUtils.isDownloadConfirmed(downloadManager, id) : false,
				updatableDeps: driveItem ? getUpdatableDependencyDetails(driveItem, downloadManager) : 'n/a',
			});
		});
	}

	if (!needsDownload) {
		logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.DownloadImportantAssets, undefined, {
			reason: OfflineDownloadReason.NeedsDownloadFalse,
		});
		// Nothing to download; clear the IMPORTANT group if a previous run left it stuck "Started".
		if (downloadManager.downloadGroups.important.status === DownloadGroupStatus.Started) {
			dispatch(finishDownloadGroup(DownloadGroupName.IMPORTANT));
		}
		return Q.resolve({});
	}

	StatsUtils.trackDownloadImportants();
	dispatch(startDownloadGroup(DownloadGroupName.IMPORTANT));

	const downloadPromises = importantAssetIds
		.map(importantAssetId => dispatch(downloadRecursively(parseInt(importantAssetId, 10), DownloadMode.Requested, false, true)));

	return allSettled(downloadPromises)
		.then(() => {
			if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
				return {};
			}

			return dispatch(backfillIncompleteDependencies());
		})
		.then(() => {
			if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
				return {};
			}

			return dispatch(repairUnconfirmedDownloads());
		})
		.finally(() => {
			logOfflineDownload(OfflineDownloadDebugAction.PhaseEnd, OfflineDownloadPhase.DownloadImportantAssets);
			dispatch(finishDownloadGroup(DownloadGroupName.IMPORTANT));
		});
};

const downloadSelectedAssets = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const {
		downloadManager: {
			pendingSelectedDownloads,
		},
	} = getState();
	StatsUtils.trackDownloadSelected();

	dispatch(startDownloadGroup(DownloadGroupName.SELECTED));

	const pendingSelectedDownloadsIds: string[] = Object.keys(pendingSelectedDownloads);

	const downloadPromises = pendingSelectedDownloadsIds
		.map(pendingSelectedDownloadsId =>
			dispatch(downloadRecursively(parseInt(pendingSelectedDownloadsId, 10), DownloadMode.Requested, true))
		);

	return allSettled(downloadPromises)
		.then(() => dispatch(reconcileIncompleteDownloads()));
};

const downloadRecursively = (driveItemId: number, mode: DownloadMode, isManualUpdate: boolean = false, isImportant = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			return Q.Promise.resolve();
		}

		const {
			driveList: { driveItems },
			downloadManager,
		} = getState();
		const { downloadGroups } = downloadManager;

		let driveItem = driveItems[driveItemId];

		// If driveItem is not found in current state, try to fetch it from cache
		// This can happen when Continue is clicked during content structure update
		if (!driveItem) {
			return Q.Promise.resolve(IndexedDBManager.getStoreDriveList())
			.then((cachedDriveList) => {
				if (cachedDriveList && cachedDriveList.driveItems[driveItemId]) {
					driveItem = cachedDriveList.driveItems[driveItemId];
					// Dispatch to add it to current state so it's available for future calls
					dispatch(receiveDriveItems({ [driveItemId]: driveItem }));
				}
				return driveItem;
			})
			.catch(() => {
				// If cache fetch fails, return undefined to continue with error handling below
				return undefined;
			})
			.then((resolvedDriveItem) => {
				if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
					return Q.Promise.resolve();
				}

				// Check again after trying cache
				if (!resolvedDriveItem && DownloadManagerUtils.getDownloadingGroup(downloadGroups) === DownloadGroupName.SELECTED) {
					dispatch(removePendingSelectedDownload(driveItemId)).then();
					return Q.Promise.resolve();
				}

				if (!resolvedDriveItem) {
					dispatch(removeDownloadInProgressItem(driveItemId));
					return Q.Promise.resolve();
				}

				driveItem = resolvedDriveItem;
				return continueDownloadRecursively(
					driveItem, driveItemId, mode, isImportant, downloadManager, dispatch, gallerySessionId);
			});
		}

		return continueDownloadRecursively(
			driveItem, driveItemId, mode, isImportant, downloadManager, dispatch, gallerySessionId);
	};

const continueDownloadRecursively = (
	driveItem: Api.DriveFolderInfo,
	driveItemId: number,
	mode: DownloadMode,
	isImportant: boolean,
	downloadManager: DownloadManagerState,
	dispatch: Dispatch<StoreState>,
	gallerySessionId: number,
): Q.Promise<{} | void> => {
	if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
		return Q.Promise.resolve();
	}

	const itemFromDownloadManager = downloadManager.items[driveItem.id]
		? downloadManager.items[driveItem.id]
		: undefined;
	let promise: Q.Promise<{} | void> = Q.Promise.resolve();

	if (!UserAgentUtils.isOnline()) {
		dispatch(createNotification('info.offline_download_multiple_assets'));
		return Q.Promise.reject({});
	}

	const { resources } = driveItem;

	if (DriveItemUtils.isDownloadableItemType(driveItem)) {
		if (itemFromDownloadManager) {
			if (shouldUseImportantOfflineShortcut(downloadManager, driveItem, isImportant)) {
				logOfflineDownload(OfflineDownloadDebugAction.Skip, OfflineDownloadPhase.DownloadRecursively, driveItem.id, {
					reason: OfflineDownloadReason.ImportantOfflineShortcut,
				});
				return promise;
			}

			logOfflineDownload(OfflineDownloadDebugAction.DepWalk, OfflineDownloadPhase.DownloadRecursively, driveItem.id, {
				reason: OfflineDownloadReason.RequestMobileAppDownloadUpdatable,
				isImportant,
			});
			promise = promise
				.then(() => {
					return dispatch(requestMobileAppDownload(
						driveItem,
						mode,
						undefined,
						true,
						false,
					));
				});
		} else {
			promise = promise.then(() => dispatch(requestMobileAppDownload(driveItem, mode)));
		}
	} else {
		Q.Promise.resolve();
	}

	if (resources) {
		// Ensure that each resource is processed sequentially
		promise = resources.reduce((acc, resource) => {
			return acc.then(() => {
				if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
					return Q.Promise.resolve();
				}

				return dispatch(downloadRecursively(resource.id, mode));
			});
		}, promise);
	}

	return promise;
};

const removePendingSelectedDownload = (driveItemId: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const {
			downloadManager: {
				pendingSelectedDownloads,
			},
		} = getState();

		const pendingSelectedDownloadsIds = Object.keys(pendingSelectedDownloads).map(key => Number(key));

		if (pendingSelectedDownloadsIds.includes(driveItemId)) {
			return dispatch(removePendingSelectedDownloadItem(driveItemId))
				.then(() => {
					// Get the updated state after removing the item
					const state = getState();
					const pendingSelectedDownloadsIdsCount = Object.keys(state.downloadManager.pendingSelectedDownloads).length;

					if (pendingSelectedDownloadsIdsCount === 0) {
						dispatch(finishDownloadGroup(DownloadGroupName.SELECTED));
						dispatch(resetTotalSelectedDownloadsInManager());
					}
				});
		} else {
			return Promise.resolve();
		}
	};

export {
	requestWebDownloadItem,
	requestMobileAppDownload,
	downloadRecursively,
	downloadAll,
	downloadImportantAssets,
	getDependencyIds,
	getDependenciesWithVersions,
	updateDriveItemWithDependencies,
	downloadSelectedAssets,
	getInitialDependenciesUpdates,
	refreshDownloadedDependencyVersions,
	syncOfflineDependenciesAfterContentRefresh,
	backfillIncompleteDependencies,
	confirmDownloadsOnDisk,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/download.ts